import { useState } from "react";
import { generateUSAFormationPDF } from "./pdfGenerator";
import companyLogo from "../../Assets/BooksCompl.png";
import "./USAFormation.css";

const QuestionForm = ({
  onClose, onSubmit, initialAnswers, initialComments, isSubmitted, companyType = "LLC"
}) => {
  const [answers, setAnswers] = useState(initialAnswers || {});
  const [comments, setComments] = useState(initialComments || {});
  const [expanded, setExpanded] = useState({});
  const [selectedBusinessType, setSelectedBusinessType] = useState(companyType);

  const cCorpQuestions = {
    "Basic Information": [
      "What Type of Business Would You Like To Start? (LLC or C-Corp)",
      "In which U.S. state do you want to incorporate the company?",
      "What Would You Like Your Business Name To Be?",
      "Business Purpose (Be Specific)",
      "Physical Address You Want On Record With The State: (Our Registered Agent Address or Your Mailing Address)",
      "Mailing Address You Want On Record With The State: (Our Registered Agent Address or Your Mailing Address)"
    ],
    "Management - President": [
      "First Name",
      "Last Name",
      "Address To Record With The State: (Our Registered Agent Address or Your Mailing Address)"
    ],
    "Management - Secretary": [
      "First Name", 
      "Last Name",
      "Address To Record With The State: (Our Registered Agent Address or Your Mailing Address)"
    ],
    "Management - Treasurer": [
      "First Name",
      "Last Name", 
      "Address To Record With The State: (Our Registered Agent Address or Your Mailing Address)",
    ],
    "Shares & Other Informations": [
      "Number of Authorized Shares",
      "Par Value",
      "All officers are directors"
    ],
    "Responsible Party": [
      "First Name",
      "Last Name",
      "Email",
      "Phone Number (USA)",
      "India Address - Line1",
      "India Address - Line2", 
      "City",
      "State",
      "Zipcode",
      "Country",
      "You have, or you expect to have, any employees who will receive Forms W-2 in the next 12 months. (Yes or No)",
      "Fiscal Year-End Month"
    ]
  };

  const llcQuestions = {
    "Basic Information": [
      "What Type of Business Would You Like To Start? (LLC or C-Corp)",
      "In which U.S. state do you want to incorporate the company?",
      "What Would You Like Your Business Name To Be?",
      "Business Purpose (Be Specific)",
      "Physical Address You Want On Record With The State: (Our Registered Agent Address or Your Mailing Address)",
      "Mailing Address You Want On Record With The State: (Our Registered Agent Address or Your Mailing Address)"
    ],
    "Partners Information - Partner 1": [
      "First Name",
      "Last Name",
      "Percentage Holding"
    ],
    "Partners Information - Partner 2": [
      "First Name",
      "Last Name", 
      "Percentage Holding"
    ],
    "Partners Information - Partner 3": [
      "First Name",
      "Last Name",
      "Percentage Holding"
    ],
    "Responsible Party": [
      "First Name",
      "Last Name",
      "Email",
      "Phone Number (USA)",
      "India Address - Line1",
      "India Address - Line2",
      "City", 
      "State",
      "Zipcode",
      "Country",
      "You have, or you expect to have, any employees who will receive Forms W-2 in the next 12 months. (Yes or No)"
    ]
  };

  const formQuestions = selectedBusinessType === "C-Corp" ? cCorpQuestions : llcQuestions;

  const totalQuestions = Object.values(formQuestions).reduce(
    (total, section) => total + section.length,
    0
  );

  const handleAnswer = (index, value) => {
    if (!isSubmitted) {
      setAnswers((prev) => ({ ...prev, [index]: value }));
    }
  };

  const handleComment = (index, value) => {
    if (!isSubmitted) {
      setComments((prev) => ({ ...prev, [index]: value }));
    }
  };

  const toggleQuestion = (question) => {
    if (!isSubmitted) {
      setExpanded((prev) => ({ ...prev, [question]: !prev[question] }));
    }
  };

  const calculateProgress = () => {
    return Object.keys(answers).filter((k) => answers[k] !== undefined).length;
  };

  const calculateSectionProgress = (section) => {
    return formQuestions[section].filter((_, index) => answers[`${section}-${index}`] !== undefined && answers[`${section}-${index}`] !== "comment").length;
  };

  const handleSave = () => {
    if (!isSubmitted) {
      const currentProgress = calculateProgress();
      onSubmit({ 
        answers: { 
          ...answers,
          'businessType': selectedBusinessType // Ensure business type is always included
        }, 
        comments 
      }, false);
      alert(`Saved progress: ${currentProgress}/${totalQuestions} questions answered`);
    }
  };

  const handleConfirm = async () => {
    const currentProgress = calculateProgress();
    if (currentProgress < 1) {
      alert("Please answer all questions.");
      return;
    }

    if (!isSubmitted) {
      try {
        const pdfBlob = generateUSAFormationPDF(
          formQuestions,
          answers,
          comments,
          selectedBusinessType,
          companyLogo
        );

        const pdfFormData = new FormData();
        const pdfName = `USA Company Formation Checklist - ${selectedBusinessType}.pdf`;
        pdfFormData.append('pdf', pdfBlob, pdfName);

        const pdfToken = localStorage.getItem("token");
        const pdfResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/upload-pdf`, {
          method: "POST",
          body: pdfFormData,
          headers: {
            Authorization: `Bearer ${pdfToken}`,
          },
        });

        if (!pdfResponse.ok) {
          throw new Error("Failed to upload PDF");
        }

        const pdfResult = await pdfResponse.json();

        onSubmit({
          answers: {
            ...answers,
            'businessType': selectedBusinessType // Ensure business type is always included
          },
          comments,
          pdfUrl: pdfResult.pdfUrl
        }, true);

        alert("Form submitted successfully!");
      } catch (error) {
        console.error("Error in form submission:", error);
        alert(`Failed to submit form: ${error.message}`);
      }
    }
  };

  const renderQuestionInput = (questionTitle, subIndex, subQuestion) => {
    const questionKey = `${questionTitle}-${subIndex}`;
    
    // Special handling for business type selection
    if (subQuestion.includes("What Type of Business Would You Like To Start?")) {
      return (
        <div className="usa-company-form-options">
          {["LLC", "C-Corp"].map((option) => (
            <button
              key={option}
              className={`usa-company-form-option ${answers[questionKey] === option ? 'usa-company-form-selected-yes' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleAnswer(questionKey, option);
                setSelectedBusinessType(option);
              }}
              disabled={isSubmitted}
              aria-label={`Select ${option}`}
            >
              {option}
            </button>
          ))}
        </div>
      );
    }

    // Special handling for Yes/No questions
    if (subQuestion.includes("Yes or No") || subQuestion.includes("employees who will receive Forms W-2")) {
      return (
        <div className="usa-company-form-options">
          {["Yes", "No"].map((option) => (
            <button
              key={option}
              className={`usa-company-form-option ${answers[questionKey] === option ? 'usa-company-form-selected-yes' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleAnswer(questionKey, option);
              }}
              disabled={isSubmitted}
              aria-label={`Select ${option}`}
            >
              {option}
            </button>
          ))}
        </div>
      );
    }

    // Special handling for address selection questions
    if (subQuestion.includes("Our Registered Agent Address or Your Mailing Address")) {
      return (
        <div className="usa-company-form-options">
          {["Our Registered Agent Address", "Your Mailing Address"].map((option) => (
            <button
              key={option}
              className={`usa-company-form-option ${answers[questionKey] === option ? 'usa-company-form-selected-yes' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleAnswer(questionKey, option);
              }}
              disabled={isSubmitted}
              aria-label={`Select ${option}`}
            >
              {option}
            </button>
          ))}
          <button
            className={`usa-company-form-option ${answers[questionKey] === "comment" ? 'usa-company-form-selected-comment' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleAnswer(questionKey, "comment");
            }}
            disabled={isSubmitted}
            aria-label="Add custom address"
          >
            CUSTOM
          </button>
        </div>
      );
    }

    // Default comment input for all other questions
    return (
      <div className="usa-company-form-options">
        <button
          className={`usa-company-form-option ${answers[questionKey] === "comment" ? 'usa-company-form-selected-comment' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            handleAnswer(questionKey, "comment");
          }}
          disabled={isSubmitted}
          aria-label="Add comment"
        >
          COMMENT
        </button>
      </div>
    );
  };

  return (
    <div className="usa-company-form-modal">
      <div className="usa-company-form-modal-content">

        <div className="usa-company-form-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
            <h2 className="usa-company-form-title" style={{ fontSize: 'clamp(1.1rem, 4vw, 1.5rem)', margin: 0 }}>
              USA Company Formation Checklist
            </h2>
            <div className="usa-company-form-progress" style={{ 
              fontSize: '0.9em', 
              color: '#4a5568',
              backgroundColor: '#f7fafc',
              padding: '0.4rem 0.8rem',
              borderRadius: '9999px',
              border: '1px solid #e2e8f0'
            }}>
              {calculateProgress()}/{totalQuestions}
            </div>
          </div>
        </div>

        <div className="usa-company-form-questions-container">
          {Object.keys(formQuestions).map((questionTitle, index) => {
            const sectionLetter = String.fromCharCode(65 + index);
            const sectionProgress = calculateSectionProgress(questionTitle);
            return (  
              <div key={index} className="usa-company-form-question" onClick={() => toggleQuestion(questionTitle)}>
                <div className="usa-company-form-question-header">
                  <span className="usa-company-form-question-number">{sectionLetter}.</span>
                  <span className="usa-company-form-question-title">{questionTitle}</span>
                  <span className="usa-company-form-section-progress">
                    {sectionProgress}/{formQuestions[questionTitle].length}
                  </span>
                </div>
                {expanded[questionTitle] && (
                  <div className="usa-company-form-sub-questions">
                    {formQuestions[questionTitle].map((subQuestion, subIndex) => (
                      <div key={subIndex} className="usa-company-form-sub-question">
                        <p className="usa-company-form-sub-question-text">
                          <span className="usa-company-form-sub-question-number">{subIndex + 1}.</span> {subQuestion}
                          {/* Add notes for specific questions */}
                          {subQuestion.includes("President") && subQuestion.includes("First Name") && (
                            <small style={{display: 'block', color: '#666', fontStyle: 'italic', marginTop: '5px'}}>
                              * Note: The President of a corporation is basically the operations manager. The person in charge of day to day operations of the company.
                            </small>
                          )}
                          {subQuestion.includes("Secretary") && subQuestion.includes("First Name") && (
                            <small style={{display: 'block', color: '#666', fontStyle: 'italic', marginTop: '5px'}}>
                              * Note: The Secretary is in charge of documenting corporate transactions of importance like amendments to the company, filing annual reports, business licenses, and any kind of private documentation like shareholders.
                            </small>
                          )}
                          {subQuestion.includes("Treasurer") && subQuestion.includes("First Name") && (
                            <small style={{display: 'block', color: '#666', fontStyle: 'italic', marginTop: '5px'}}>
                              * Note: The Treasurer is in charge of the financial transactions of the company.
                            </small>
                          )}
                        </p>
                        {renderQuestionInput(questionTitle, subIndex, subQuestion)}
                        {(answers[`${questionTitle}-${subIndex}`] === "comment" || 
                          (answers[`${questionTitle}-${subIndex}`] === "Your Mailing Address")) && (
                          <textarea
                            placeholder="Add your response..."
                            value={comments[`${questionTitle}-${subIndex}`] || ""}
                            onChange={(e) => handleComment(`${questionTitle}-${subIndex}`, e.target.value)}
                            className="usa-company-form-comment-box"
                            disabled={isSubmitted}
                            onClick={(e) => e.stopPropagation()}
                            aria-label="Comment input"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="usa-company-form-actions">
          <button className="usa-company-form-btn usa-company-form-close" onClick={onClose}>
            Close
          </button>
          <div className="usa-company-form-right-buttons">
            <button
              className="usa-company-form-btn usa-company-form-save"
              onClick={handleSave}
              disabled={isSubmitted}
            >
              Save
            </button>
            <button
              className="usa-company-form-btn usa-company-form-confirm"
              onClick={handleConfirm}
              disabled={isSubmitted}
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionForm;